import React, { useState, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import {
  ADD_POWER_METER_SETTINGS_URL,
  GET_POWER_METER_SETTINGS_URL,
  UPDATE_POWER_METER_SETTINGS_URL,
  getBillerAddressesURL,
  getBankDetailsURL,
} from "../../../../../../utils/urls";
import { getGLAccountsURL, getFacilityPaybillsDetailsURL } from "../../../../../../utils/urls";
import { useFacilityStore } from "../../../../../../app/z-store/store";

const PowerMeterSettingsTab = () => {
  const facilityId = useFacilityStore((state) => state.facilityId);

  const [formData, setFormData] = useState({
    minAmount: 0,
    maxAmount: 50000,
    gracePeriod: 10,
    invoiceDay: 3,
    enforcePayment: "",
    minimumPaymentAmount: 0,
    tariff: "",
    tariffAmount: 0,
    fixedTariffAmount: 0,
    meterLoan: 0,
    notifications: false,
    dailyNotifications: false,
    weeklyNotifications: false,
    allNotifications: false,
    otherCharges: "",
    lowThreshold: 0,
    highThreshold: 0,
    billerAddressId: "",
    paymentMethods: {
      mobilePayment: {
        status: false,
        paymentId: ""
      },
      bankPayment: {
        status: false,
        paymentId: ""
      }
    },
    glAccounts: {
      invoice: { debit: "", credit: "" },
      payment: { debit: "", credit: "" }
    }
  });

  const [settingsExist, setSettingsExist] = useState(false);

  // GL Accounts
  const [glAccounts, setGlAccounts] = useState([]);
  const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);

  // Biller Addresses
  const [billerAddresses, setBillerAddresses] = useState([]);
  const [loadingBillerAddresses, setLoadingBillerAddresses] = useState(false);

  // Bank Details
  const [bankDetails, setBankDetails] = useState([]);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);

  // Mobile / M-Pesa Payment Details
  const [facilityPaymentDetails, setFacilityPaymentDetails] = useState([]);
  const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);

  useEffect(() => {
    if (facilityId) {
      fetchSettings();
      fetchGLAccounts();
      fetchBillerAddresses();
      fetchBankDetails();
      fetchFacilityPaymentDetails();
    }
  }, [facilityId]);

  // ─── Fetchers ────────────────────────────────────────────────────────────────

  const fetchGLAccounts = async () => {
    try {
      setLoadingGLAccounts(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");
      if (response && response.success) {
        setGlAccounts(response.data?.data || []);
      } else {
        setGlAccounts([]);
      }
    } catch (error) {
      toastify("Error fetching GL accounts.", "error");
      setGlAccounts([]);
    } finally {
      setLoadingGLAccounts(false);
    }
  };

  const fetchBillerAddresses = async () => {
    try {
      setLoadingBillerAddresses(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, "GET");
      if (response && response.success && response.data) {
        let addresses = [];
        if (Array.isArray(response.data)) {
          addresses = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          addresses = response.data.data;
        } else if (response.data.addresses && Array.isArray(response.data.addresses)) {
          addresses = response.data.addresses;
        } else {
          addresses = [response.data];
        }
        setBillerAddresses(addresses);
      } else {
        setBillerAddresses([]);
      }
    } catch (error) {
      toastify("Error fetching biller addresses.", "error");
      setBillerAddresses([]);
    } finally {
      setLoadingBillerAddresses(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      setLoadingBankDetails(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, "GET");
      if (response && response.success && response.data) {
        let details = [];
        if (Array.isArray(response.data)) {
          details = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          details = response.data.data;
        } else if (response.data.bankDetails && Array.isArray(response.data.bankDetails)) {
          details = response.data.bankDetails;
        } else {
          details = [response.data];
        }
        setBankDetails(details);
      } else {
        setBankDetails([]);
      }
    } catch (error) {
      toastify("Error fetching bank details.", "error");
      setBankDetails([]);
    } finally {
      setLoadingBankDetails(false);
    }
  };

  const fetchFacilityPaymentDetails = async () => {
    try {
      setLoadingPaymentDetails(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getFacilityPaybillsDetailsURL}/${facilityId}`, "GET");
      if (response && response.success && response.data) {
        let details = [];
        if (Array.isArray(response.data)) {
          details = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          details = response.data.data;
        } else {
          details = [response.data];
        }
        setFacilityPaymentDetails(details);
      } else {
        setFacilityPaymentDetails([]);
      }
    } catch (error) {
      toastify("Error fetching facility payment details.", "error");
      setFacilityPaymentDetails([]);
    } finally {
      setLoadingPaymentDetails(false);
    }
  };

  const fetchSettings = async () => {
    try {
      if (!facilityId) return;

      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_settings/get_settings/${facilityId}`,
        "GET"
      );

      if (response && response.data && response.data.data) {
        const isDefaultSettings = response.isDefault === true;
        const hasActualId = response.data.data._id || response.data.data.id;
        const settingsActuallyExist = !isDefaultSettings && hasActualId;
        setSettingsExist(settingsActuallyExist);

        const d = response.data.data;

        // GL accounts
        let processedGLAccounts = { invoice: { debit: "", credit: "" }, payment: { debit: "", credit: "" } };
        if (response.data.glAccountsDetails) {
          const gl = response.data.glAccountsDetails;
          processedGLAccounts = {
            invoice: { debit: gl.invoice?.debit?.id || "", credit: gl.invoice?.credit?.id || "" },
            payment: { debit: gl.payment?.debit?.id || "", credit: gl.payment?.credit?.id || "" }
          };
        } else if (d.glAccounts) {
          const gl = d.glAccounts;
          processedGLAccounts = {
            invoice: {
              debit: gl.invoice?.debit?._id || gl.invoice?.debit || "",
              credit: gl.invoice?.credit?._id || gl.invoice?.credit || ""
            },
            payment: {
              debit: gl.payment?.debit?._id || gl.payment?.debit || "",
              credit: gl.payment?.credit?._id || gl.payment?.credit || ""
            }
          };
        }

        // Payment methods
        const paymentMethods = d.paymentMethods || { mobilePayment: { status: false, paymentId: "" }, bankPayment: { status: false, paymentId: "" } };

        setFormData({
          minAmount: d.minAmount ?? 0,
          maxAmount: d.maxAmount ?? 50000,
          gracePeriod: d.gracePeriod ?? 10,
          invoiceDay: d.invoiceDay ?? 3,
          enforcePayment: d.enforcePayment || "",
          minimumPaymentAmount: d.minimumPaymentAmount ?? 0,
          tariff: d.tariff || "",
          tariffAmount: d.tariffAmount ?? 0,
          fixedTariffAmount: d.fixedTariffAmount ?? 0,
          meterLoan: d.meterLoan ?? 0,
          notifications: Boolean(d.notifications),
          dailyNotifications: Boolean(d.dailyNotifications),
          weeklyNotifications: Boolean(d.weeklyNotifications),
          allNotifications: Boolean(d.allNotifications),
          otherCharges: d.otherCharges || "",
          lowThreshold: d.lowThreshold ?? 0,
          highThreshold: d.highThreshold ?? 0,
          billerAddressId: d.billerAddressId?._id || d.billerAddressId || "",
          paymentMethods: {
            mobilePayment: {
              status: Boolean(paymentMethods.mobilePayment?.status),
              paymentId: paymentMethods.mobilePayment?.paymentId?._id || paymentMethods.mobilePayment?.paymentId || ""
            },
            bankPayment: {
              status: Boolean(paymentMethods.bankPayment?.status),
              paymentId: paymentMethods.bankPayment?.paymentId?._id || paymentMethods.bankPayment?.paymentId || ""
            }
          },
          glAccounts: processedGLAccounts
        });
      } else {
        setSettingsExist(false);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      if (error.response?.status === 404) {
        setSettingsExist(false);
      } else {
        toastify("Error fetching settings", "error");
        setSettingsExist(false);
      }
    }
  };

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    try {
      if (!facilityId) {
        toastify("No facility ID available", "error");
        return;
      }

      if (formData.tariff === "yes") {
        if (
          !formData.glAccounts.invoice.debit || !formData.glAccounts.invoice.credit ||
          !formData.glAccounts.payment.debit || !formData.glAccounts.payment.credit
        ) {
          toastify("Please select all GL accounts when tariff is enabled", "error");
          return;
        }
      }

      const dataToSend = { ...formData, facilityId };

      let response;
      if (settingsExist) {
        response = await makeRequest2(
          `/api/app/power_meter_management/controllers/power_settings/edit_settings/${facilityId}`,
          "PUT",
          dataToSend
        );
      } else {
        response = await makeRequest2(
          `/api/app/power_meter_management/controllers/power_settings/add_settings`,
          "POST",
          dataToSend
        );
      }

      if (response && (response.success || response.data)) {
        toastify(`Settings ${settingsExist ? "updated" : "saved"} successfully`, "success");
        setSettingsExist(true);
        fetchSettings();
      } else {
        toastify(response?.error || response?.message || "Failed to save settings", "error");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toastify("Error saving settings", "error");
    }
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleGLAccountChange = (section, type, value) => {
    setFormData(prev => ({
      ...prev,
      glAccounts: { ...prev.glAccounts, [section]: { ...prev.glAccounts[section], [type]: value } }
    }));
  };

  const handleBankPaymentSelection = (bankId, enabled) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        bankPayment: { status: enabled, paymentId: enabled ? bankId : "" }
      }
    }));
  };

  const handleMobilePaymentSelection = (paymentId, enabled) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        mobilePayment: { status: enabled, paymentId: enabled ? paymentId : "" }
      }
    }));
  };

  const handleAllNotificationsChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      allNotifications: checked,
      dailyNotifications: checked,
      weeklyNotifications: checked
    }));
  };

  const handleNotificationTypeChange = (type, checked) => {
    setFormData(prev => {
      const updated = { ...prev, [type]: checked };
      updated.allNotifications =
        (type === "dailyNotifications" ? checked : prev.dailyNotifications) &&
        (type === "weeklyNotifications" ? checked : prev.weeklyNotifications);
      return updated;
    });
  };

  const isLoadingAny = loadingGLAccounts || loadingBillerAddresses || loadingBankDetails || loadingPaymentDetails;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title mb-0">Power Meter Settings</h4>
      </div>
      <div className="card-body">
        <div className="settings-container">

          {/* ── Biller Address ── */}
          <h5 className="section-header mb-3">Biller Address</h5>
          <div className="row mb-4">
            <div className="col-12 mb-2">
              {loadingBillerAddresses && (
                <div className="alert alert-info">Loading biller addresses...</div>
              )}
              {!loadingBillerAddresses && billerAddresses.length === 0 && (
                <div className="alert alert-warning">
                  No biller addresses found. Please create one in the Biller Address tab first.
                </div>
              )}
            </div>
            {billerAddresses.length > 0 && (
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold">Select Biller Address</label>
                  <select
                    className="form-control w-100"
                    value={formData.billerAddressId}
                    onChange={(e) => setFormData(prev => ({ ...prev, billerAddressId: e.target.value }))}
                  >
                    <option value="">-- Select Biller Address --</option>
                    {billerAddresses.map((address) => (
                      <option key={address._id} value={address._id}>
                        {address.name} – {address.companyName} ({address.city})
                        {address.isDefault ? " (Default)" : ""}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    The selected address will appear on power invoices
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* ── Payment Methods ── */}
          <h5 className="section-header mb-3">Payment Methods</h5>
          <div className="row mb-4">
            <div className="col-12 mb-2">
              <p className="text-muted">
                Configure which payment methods are available for customers paying power bills.
              </p>
            </div>

            {/* Bank Payment */}
            <div className="col-md-6">
              <div className="card border">
                <div className="card-header bg-light">
                  <h6 className="mb-0">Bank Payment</h6>
                </div>
                <div className="card-body">
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="enableBankPayment"
                      checked={formData.paymentMethods.bankPayment.status}
                      onChange={(e) =>
                        handleBankPaymentSelection(
                          formData.paymentMethods.bankPayment.paymentId,
                          e.target.checked
                        )
                      }
                    />
                    <label className="form-check-label fw-bold" htmlFor="enableBankPayment">
                      Enable Bank Payment
                    </label>
                  </div>
                  {formData.paymentMethods.bankPayment.status && (
                    <div className="form-group">
                      <label className="form-label fw-bold">Select Bank Account</label>
                      {loadingBankDetails && (
                        <div className="alert alert-info py-2">Loading bank details...</div>
                      )}
                      {!loadingBankDetails && bankDetails.length === 0 && (
                        <div className="alert alert-warning py-2">
                          No bank details found. Please add bank details first.
                        </div>
                      )}
                      {bankDetails.length > 0 && (
                        <select
                          className="form-control"
                          value={formData.paymentMethods.bankPayment.paymentId}
                          onChange={(e) => handleBankPaymentSelection(e.target.value, true)}
                        >
                          <option value="">-- Select Bank Account --</option>
                          {bankDetails.map((bank) => (
                            <option key={bank._id} value={bank._id}>
                              {bank.bankName} – {bank.accountName} ({bank.accountNumber})
                              {bank.isDefault ? " (Default)" : ""}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Payment */}
            <div className="col-md-6">
              <div className="card border">
                <div className="card-header bg-light">
                  <h6 className="mb-0">Mobile Payment (M-Pesa)</h6>
                </div>
                <div className="card-body">
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="enableMobilePayment"
                      checked={formData.paymentMethods.mobilePayment.status}
                      onChange={(e) =>
                        handleMobilePaymentSelection(
                          formData.paymentMethods.mobilePayment.paymentId,
                          e.target.checked
                        )
                      }
                    />
                    <label className="form-check-label fw-bold" htmlFor="enableMobilePayment">
                      Enable Mobile Payment
                    </label>
                  </div>
                  {formData.paymentMethods.mobilePayment.status && (
                    <div className="form-group">
                      <label className="form-label fw-bold">Select Paybill Configuration</label>
                      {loadingPaymentDetails && (
                        <div className="alert alert-info py-2">Loading payment details...</div>
                      )}
                      {!loadingPaymentDetails && facilityPaymentDetails.length === 0 && (
                        <div className="alert alert-warning py-2">
                          No M-Pesa paybill configuration found. Please add one first.
                        </div>
                      )}
                      {facilityPaymentDetails.length > 0 && (
                        <select
                          className="form-control"
                          value={formData.paymentMethods.mobilePayment.paymentId}
                          onChange={(e) => handleMobilePaymentSelection(e.target.value, true)}
                        >
                          <option value="">-- Select Paybill Configuration --</option>
                          {facilityPaymentDetails.map((payment) => (
                            <option key={payment._id} value={payment._id}>
                              Paybill: {payment.shortCode} – {payment.module}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Billing Amounts ── */}
          <h5 className="section-header mb-3">Billing Amounts</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Minimum Amount (KES)</label>
                <InputNumber
                  className="w-100"
                  value={formData.minAmount}
                  onChange={(e) => setFormData(prev => ({ ...prev, minAmount: e.value }))}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Maximum Amount (KES)</label>
                <InputNumber
                  className="w-100"
                  value={formData.maxAmount}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxAmount: e.value }))}
                />
              </div>
            </div>
          </div>

          {/* ── Suspicious Meter Reading Thresholds ── */}
          <h5 className="section-header mb-3">Suspicious Meter Reading Thresholds</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Low Threshold (kWh)</label>
                <InputNumber
                  className="w-100"
                  value={formData.lowThreshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, lowThreshold: e.value }))}
                  min={0}
                  tooltip="Alert if meter reading falls below this threshold"
                  tooltipOptions={{ position: "top" }}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">High Threshold (kWh)</label>
                <InputNumber
                  className="w-100"
                  value={formData.highThreshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, highThreshold: e.value }))}
                  min={0}
                  tooltip="Alert if meter reading exceeds this threshold"
                  tooltipOptions={{ position: "top" }}
                />
              </div>
            </div>
          </div>

          {/* ── Billing Schedule ── */}
          <h5 className="section-header mb-3">Billing Schedule</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Grace Period (Days)</label>
                <InputNumber
                  className="w-100"
                  value={formData.gracePeriod}
                  onChange={(e) => setFormData(prev => ({ ...prev, gracePeriod: e.value }))}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Invoice Day (Days before month end)</label>
                <InputNumber
                  className="w-100"
                  value={formData.invoiceDay}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceDay: e.value }))}
                  min={0}
                  max={30}
                  tooltip="Number of days before the end of the month when invoices are generated"
                  tooltipOptions={{ position: "top" }}
                />
              </div>
            </div>
          </div>

          {/* ── Payment Enforcement ── */}
          <h5 className="section-header mb-3">Payment Enforcement</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enforce Minimum Payment</label>
                <select
                  className="form-control w-100"
                  value={formData.enforcePayment}
                  onChange={(e) => setFormData(prev => ({ ...prev, enforcePayment: e.target.value }))}
                >
                  <option value="">Select an option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.enforcePayment === "yes" && (
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold">Minimum Payment Amount (KES)</label>
                  <InputNumber
                    className="w-100"
                    value={formData.minimumPaymentAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, minimumPaymentAmount: e.value }))}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Tariff Settings ── */}
          <h5 className="section-header mb-3">Tariff Settings</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enable Tariff</label>
                <select
                  className="form-control w-100"
                  value={formData.tariff}
                  onChange={(e) => setFormData(prev => ({ ...prev, tariff: e.target.value }))}
                >
                  <option value="">Select Tariff</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.tariff === "yes" && (
              <>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Tariff Amount (Per kWh)</label>
                    <InputNumber
                      className="w-100"
                      value={formData.tariffAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, tariffAmount: e.value }))}
                      maxFractionDigits={2}
                    />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Fixed Amount (Per Meter) (Monthly)</label>
                    <InputNumber
                      className="w-100"
                      value={formData.fixedTariffAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, fixedTariffAmount: e.value }))}
                    />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Meter Loan Amount (KES)</label>
                    <InputNumber
                      className="w-100"
                      value={formData.meterLoan}
                      onChange={(e) => setFormData(prev => ({ ...prev, meterLoan: e.value }))}
                      tooltip="Monthly amount charged for meter financing"
                      tooltipOptions={{ position: "top" }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── GL Account Configuration ── */}
          <h5 className="section-header mb-1">GL Account Configuration</h5>
          <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
            <i className="pi pi-info-circle me-1"></i>
            Optional — configure GL accounts for accounting entries if required.
          </p>
          <div className="row mb-4">
            <div className="col-12 mb-3">
              {loadingGLAccounts && <div className="alert alert-info">Loading GL accounts...</div>}
              {!loadingGLAccounts && glAccounts.length === 0 && (
                <div className="alert alert-secondary">
                  No GL accounts found. You can create GL accounts separately — this section is optional.
                </div>
              )}
            </div>

            {glAccounts.length > 0 && (
              <>
                <div className="col-12 mb-3">
                  <h6 className="text-primary">
                    Power Invoice GL Entries{" "}
                    <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
                      Optional
                    </span>
                  </h6>
                  <p className="text-muted small">Configure GL accounts for power billing invoice entries</p>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Invoice Debit Account</label>
                    <select
                      className="form-control w-100"
                      value={formData.glAccounts?.invoice?.debit || ""}
                      onChange={(e) => handleGLAccountChange("invoice", "debit", e.target.value)}
                    >
                      <option value="">-- Select Debit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when power invoices are generated</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Invoice Credit Account</label>
                    <select
                      className="form-control w-100"
                      value={formData.glAccounts?.invoice?.credit || ""}
                      onChange={(e) => handleGLAccountChange("invoice", "credit", e.target.value)}
                    >
                      <option value="">-- Select Credit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when power invoices are generated</small>
                  </div>
                </div>

                <div className="col-12 mb-3 mt-4">
                  <h6 className="text-primary">
                    Power Payment GL Entries{" "}
                    <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
                      Optional
                    </span>
                  </h6>
                  <p className="text-muted small">Configure GL accounts for power payment entries</p>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Payment Debit Account</label>
                    <select
                      className="form-control w-100"
                      value={formData.glAccounts?.payment?.debit || ""}
                      onChange={(e) => handleGLAccountChange("payment", "debit", e.target.value)}
                    >
                      <option value="">-- Select Debit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when power payments are received</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Payment Credit Account</label>
                    <select
                      className="form-control w-100"
                      value={formData.glAccounts?.payment?.credit || ""}
                      onChange={(e) => handleGLAccountChange("payment", "credit", e.target.value)}
                    >
                      <option value="">-- Select Credit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when power payments are received</small>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── KPLC Charges ── */}
          <h5 className="section-header mb-3">KPLC Charges</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enable KPLC Charges</label>
                <select
                  className="form-control w-100"
                  value={formData.otherCharges}
                  onChange={(e) => setFormData(prev => ({ ...prev, otherCharges: e.target.value }))}
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.otherCharges === "yes" && (
              <div className="col-12 mt-3">
                <div className="alert alert-info">
                  <div className="d-flex align-items-start">
                    <i className="pi pi-info-circle me-2 mt-1"></i>
                    <div>
                      <h6 className="mb-2">KPLC Charges Enabled</h6>
                      <p className="mb-0">
                        When enabled, the system will automatically apply the KPLC power charges
                        configured in the <strong>"Charges"</strong> tab. This includes:
                      </p>
                      <ul className="mb-0 mt-2">
                        <li>Fuel Cost Charge</li>
                        <li>Forex Adjustment</li>
                        <li>Inflation Adjustment</li>
                        <li>Water Resource Management Levy</li>
                        <li>Energy Regulatory Levy</li>
                        <li>Rural Electrification Levy</li>
                        <li>Value Added Tax (VAT)</li>
                      </ul>
                      <p className="mt-2 mb-0">
                        <strong>Note:</strong> Make sure you have configured the monthly KPLC charges
                        in the Charges tab before enabling this option.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Notifications ── */}
          <h5 className="section-header mb-3">Notifications</h5>
          <div className="row mb-4">
            <div className="col-12">
              <div className="form-group">
                <div className="d-flex align-items-center">
                  <Checkbox
                    inputId="notifications"
                    checked={formData.notifications}
                    onChange={(e) => setFormData(prev => ({ ...prev, notifications: e.checked }))}
                  />
                  <label htmlFor="notifications" className="ms-2 mb-0 fw-bold">
                    Enable Notifications
                  </label>
                </div>
              </div>
            </div>
            {formData.notifications && (
              <div className="col-12 mt-3">
                <div className="notification-periods p-3 border rounded">
                  <h6 className="mb-3">Notification Periods</h6>
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center">
                      <Checkbox
                        inputId="allNotifications"
                        checked={formData.allNotifications}
                        onChange={(e) => handleAllNotificationsChange(e.checked)}
                      />
                      <label htmlFor="allNotifications" className="ms-2 mb-0">Select All</label>
                    </div>
                    <div className="d-flex align-items-center">
                      <Checkbox
                        inputId="dailyNotifications"
                        checked={formData.dailyNotifications}
                        onChange={(e) => handleNotificationTypeChange("dailyNotifications", e.checked)}
                      />
                      <label htmlFor="dailyNotifications" className="ms-2 mb-0">Daily Reminders</label>
                    </div>
                    <div className="d-flex align-items-center">
                      <Checkbox
                        inputId="weeklyNotifications"
                        checked={formData.weeklyNotifications}
                        onChange={(e) => handleNotificationTypeChange("weeklyNotifications", e.checked)}
                      />
                      <label htmlFor="weeklyNotifications" className="ms-2 mb-0">Weekly Reminders</label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Save Button ── */}
          <div className="row mt-4">
            <div className="col-12">
              <Button
                label={settingsExist ? "Update Settings" : "Save Settings"}
                className="btn btn-primary w-100"
                onClick={handleSave}
                disabled={isLoadingAny}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PowerMeterSettingsTab;